/**
 * Read-adapter from the real `payroll.listPayRuns` / `payroll.getPayRun` /
 * `payroll.getPayRunJournalPreview` wire shapes to the UI's `PayRun` type
 * (C4a, #21954). Pure, no React — mirrors `wagepro_store_employees.ts`'s
 * `deriveEmployeesFromRecords` pattern exactly: `RealPayRunRecord` is the
 * permanent adapter shape (survives the #21929 employer-admin OAuth scope
 * work unchanged; only HOW records get resolved changes — see
 * `data/use_pay_runs.ts` for today's real, self-fetching resolver), and
 * `derivePayRunsFromRecords` is the permanent seam the store calls.
 *
 * ── Read-path only ─────────────────────────────────────────────────────────
 *
 * This adapter only ever maps FROM the real backend's pay-run rows TO the UI
 * shape; it is never invoked in the write direction. `createPayRun` /
 * `setPayRunLineAdjustment` / `approvePayRun` are explicitly out of scope for
 * this pass (C4b, #21955) — the wizard's mutating steps (approval, post-run
 * actions) keep using the in-memory store's existing client-local reducers
 * until those land.
 *
 * ── Why `RealPayRunRecord` bundles three calls, not one ────────────────────
 *
 * Unlike `RealEmployeeRecord` (one `listEmployees` row maps to one
 * `Employee`), a UI `PayRun` needs line-level detail (`line_items`, an
 * accurate `net_payable`) that `listPayRuns` alone doesn't return — only
 * `getPayRun` does. And the itemised EPF/ETF/PAYE statutory breakdown is
 * only available two different ways depending on status: `getPayRun`'s
 * persisted per-line columns once a run is `approved`+, or
 * `getPayRunJournalPreview`'s dry-run GL preview while it's still `draft`
 * (those columns are genuinely zero pre-approval — see
 * `apps/api/src/trpc/routers/payroll.ts`'s `getPayRunJournalPreview`).
 * `data/use_pay_runs.ts` is the one place that joins all three calls per run
 * into this bundle; this module never calls the network.
 *
 * ── Documented gaps (do not silently drop) ─────────────────────────────────
 *
 * - `pay_date` / `filing_due_on` both fall back to `period_end` — the
 *   backend has no distinct pay-date or filing-calendar concept yet (C5's
 *   filing calendar and the "pay_date/recurring schedules" gap are both
 *   documented, not built, per the issue's own out-of-scope list).
 * - Per-line `deductions` is `gross_pay_cents - net_pay_cents` (the TOTAL of
 *   every deduction, custom and statutory combined) rather than only the
 *   non-statutory `deductions_cents` column, specifically so `line_net()`
 *   (`pay_run_calculations.ts`, `gross - deductions`) always reconciles to
 *   the real persisted `net_pay_cents` instead of silently drifting from it
 *   once a run is approved and statutory withholding is posted.
 * - The itemised statutory summary (`epf_employee`/`epf_employer`/
 *   `etf_employer`/`apit_withheld`) for **draft** runs is recovered from the
 *   journal preview's GL lines by matching the four SL statutory control
 *   account codes the payroll GL posting always uses (829/831/832/833 — see
 *   `packages/jumpstone-core/src/jurisdictions/lk/coa_seed.ts`). These codes
 *   are hardcoded here (not imported from `@jumpstone/core`, which
 *   `apps/wagepro-web` does not otherwise depend on) because they are
 *   system-critical constants that do not change — the same "pin a stable
 *   default" stance `wagepro_store_employees.ts` takes for `pay_frequency`.
 *   For Indonesia today, which posts PPh21/BPJS liabilities under a
 *   different, per-entity-resolved account model (see
 *   `id_pph21_payable_account_id`/`id_bpjs_liability_account_id` on the
 *   `pay_runs` row) and whose seeded COA has no code 829/831/832/833, no
 *   line matches any of the four codes and every field is honestly `0`
 *   rather than a fabricated split — `0` reads unambiguously as "not
 *   available" here, never as a plausible-looking wrong figure. This
 *   safety is a property of TODAY'S seeded jurisdiction packs, not a
 *   structural guarantee: Canada's COA seed already assigns 831/832/833 to
 *   unrelated GST/PST control accounts. If a future jurisdiction pack gains
 *   both a payroll engine AND a custom account coded '829', this matching
 *   would silently attribute that entity's GL credit to "EPF" instead of
 *   correctly returning 0 — re-check this matching strategy before wiring
 *   payroll engines for any jurisdiction beyond Sri Lanka.
 * - `currency` is unconditionally `'LKR'` — `CurrencyCode` in `types.ts` is
 *   itself a single-value union today (WagePro has no multi-currency
 *   concept anywhere in its type system yet), so this is not a gap this
 *   adapter introduces or could resolve on its own.
 * - The statutory summary's rate fields (`epf_employee_rate` etc.) are
 *   today's only supported jurisdiction's fixed SL defaults (matching the
 *   real engine's `SL_EMPLOYEE_EPF_BPS`/`SL_EMPLOYER_EPF_BPS`/
 *   `SL_EMPLOYER_ETF_BPS` exactly) — no procedure in THIS pass's scope
 *   surfaces a real per-pay-run rate breakdown here; the entity-level
 *   statutory policy (EPF/ETF rates, APIT bands, gratuity thresholds) IS
 *   real and backend-sourced as of #22726 — see `use_payroll_policies.ts`
 *   and the Payroll Policies screen, a separate read from this pay-run
 *   summary.
 * - `approval.preparer_name` / `approval.approver_name` /
 *   `approval.approver_title` are always `''` (unknown) — the backend
 *   records `approved_by` as a user id, not a resolved display name, and no
 *   procedure in scope resolves one. The UI hides an empty name rather than
 *   printing a placeholder dash; the approver's display name, when shown at
 *   all, comes from the pay-run audit feed (`payroll.pay_run.approve`
 *   entry's `actor_display_name`), and the approval instant from
 *   `PayRun.approved_at`. `preparer_state` is always
 *   `'submitted'` (no distinct "submitted for review" step exists
 *   server-side — a draft run is implicitly ready for review the moment it
 *   exists); `approver_state` is `'approved'` once `status` has moved past
 *   `draft`, `'not_submitted'` otherwise.
 * - There is no `audit` field on `PayRun` — C4c (#21956) originally fed one
 *   from a boot-time-resolved per-run row map (`auditRowsByRunId`), but C10
 *   (#22332) deleted that eager boot-time fan-out in favour of a real,
 *   on-demand hook (`use_pay_run_audit.ts`) that the audit drawer calls
 *   directly, keyed on `PayRun.created_at` (see that field's doc comment) —
 *   a second follow-up pass (adversarial review of #22332) removed the
 *   now-fully-dead `auditRowsByRunId` parameter and `PayRun.audit` field
 *   entirely rather than leaving unused plumbing behind.
 * - `employee_count` reflects the pay run's own line count (how many
 *   employees this specific run covers), not a live headcount.
 * - Exceptions (`bank_details_incomplete` / `new_joiner_unconfigured` /
 *   `time_logged_on_unpaid_leave`) are only ever derived for `draft` runs —
 *   once a run is approved/paid/voided it is no longer editable and the
 *   exceptions gate is moot (mirrors `can_advance`'s "only the exceptions
 *   step blocks advancement" rule in `pay_run_calculations.ts`). They are
 *   derived purely client-side — zero new backend calls — and `acknowledged`
 *   always starts `false` since acknowledgement is deliberately client-local,
 *   never persisted (per the issue's own guidance). The first two come from
 *   the already-resolved `RealEmployeeRecord[]` (C3, #21934); the third
 *   (#24397) comes from the pay-run LINE's `unpaid_leave_hours_excluded` and
 *   therefore survives an unresolved employee list (it just loses the display
 *   name) — see `deriveExceptions`.
 */
import type {
  MinorUnits,
  PayPeriod,
  PayRun,
  PayRunApproval,
  PayRunException,
  PayRunLineItem,
  PayRunStatus,
  StatutorySummary,
} from './types';
import {
  isParseableIsoDate,
  type RealEmployeeRecord,
} from './wagepro_store_employees';

/** A `pay_run_lines` row, narrowed to exactly the fields this adapter reads (see `getPayRun`'s response in `apps/api/src/trpc/routers/payroll.ts`). */
export interface RealPayRunLine {
  employee_id: string;
  gross_pay_cents: number;
  deductions_cents: number;
  net_pay_cents: number;
  /** Persisted only once the parent run is `approved`+ — `0` at `draft`. */
  employee_epf_cents: number;
  /** Persisted only once the parent run is `approved`+ — `0` at `draft`. */
  employer_epf_cents: number;
  /** Persisted only once the parent run is `approved`+ — `0` at `draft`. */
  etf_cents: number;
  /** Persisted only once the parent run is `approved`+ — `0` at `draft`. */
  paye_cents: number;
  /**
   * One-time adjustment folded into `gross_pay_cents` while the run is
   * `draft` (#23309, `setPayRunLineAdjustment`). `null`/missing when none is
   * applied. Optional: pre-existing fixtures/tests never carried this
   * column, so leaving it absent falls through to the "no adjustment"
   * branch in {@link toLineItem}.
   */
  adjustment_cents?: number | null;
  adjustment_reason?: string | null;
  /**
   * Lump-sum portion of `gross_pay_cents` (#23309, `setPayRunLineLumpSum`),
   * non-negative. Optional: same fixture-compatibility rationale as
   * `adjustment_cents`.
   */
  lump_sum_cents?: number;
  /**
   * IRD Table-2 tax on a lump-sum line — real withheld income tax that the
   * payslip prints inside its single "PAYE / APIT" row alongside
   * `paye_cents` (`generatePayslip`, `apps/api/src/trpc/routers/
   * payroll.ts`). Counted as statutory here for exactly that reason.
   * Persisted only once the parent run is `approved`+; optional for the
   * same fixture-compatibility rationale as `adjustment_cents`.
   */
  apit_lump_sum_cents?: number;
  /** Indonesia PPh 21 withheld. MAY BE NEGATIVE on a December true-up refund. Optional: see `apit_lump_sum_cents`. */
  pph21_cents?: number;
  /** Indonesia employee-side BPJS total (JHT + JP + Kesehatan). Optional: see `apit_lump_sum_cents`. */
  bpjs_employee_cents?: number;
  /**
   * Indonesia Tunjangan Hari Raya — a holiday bonus that INCREASES pay
   * (`approvePayRun` persists `net = gross + thr - deductions`) but is never
   * folded into the run's `total_gross_cents`. See `deriveGrossAndNet`.
   */
  thr_cents?: number;
  /**
   * This line's snapshotted `pay_run_line_items` rows, attached by
   * `getPayRun` (#21802, C9). `[]` for a line with no assigned pay items;
   * optional so pre-existing fixtures stay valid.
   */
  items?: RealPayRunLineItemSnapshot[];
  /**
   * Hours (×10,000) `createPayRun` dropped from this line because they were
   * logged on a day covered by an approved UNPAID leave request
   * (`pay_run_lines.unpaid_leave_hours_excluded`, V344/#24397). `> 0` is the
   * tell that the timesheet and the leave register contradict each other, and
   * is what raises the `time_logged_on_unpaid_leave` exception. Optional:
   * same fixture-compatibility rationale as `adjustment_cents`, and every
   * pre-V344 line reads back 0.
   */
  unpaid_leave_hours_excluded?: number;
  /**
   * V387 signed retroactive salary difference paid on this line -- a
   * DECOMPOSITION of `gross_pay_cents` (like overtime or a bonus), never an
   * amount on top of it. `0`/absent = none. Optional: pre-V387 servers and
   * fixtures never carried this column.
   */
  back_pay_cents?: number;
  /**
   * V387 JSON `CompensationSegmentSnapshot[]` -- one entry per rate segment
   * base gross was built from. More than one entry = the salary changed inside
   * this line's period. `null`/absent on every pre-V387 line.
   */
  compensation_segments?: string | null;
}

/**
 * Whether a line's stored `compensation_segments` JSON describes MORE than one
 * rate segment, i.e. the pay changed inside the period. Tolerant by design:
 * absent, empty or unparseable JSON is simply "no change to report" -- a hint
 * on the review table must never break the whole screen.
 */
export function hasSalaryChangedInPeriod(
  compensation_segments: string | null | undefined,
): boolean {
  if (compensation_segments === null || compensation_segments === undefined) {
    return false;
  }
  try {
    const parsed: unknown = JSON.parse(compensation_segments);
    return Array.isArray(parsed) && parsed.length > 1;
  } catch (err) {
    console.error(
      '[wagepro-web] Ignoring unparseable compensation_segments on a pay run line:',
      err,
    );
    return false;
  }
}

/**
 * One `pay_run_line_items` row, narrowed to what this adapter reads.
 * `category` is the snapshotted value at computation time — `deduction`/
 * `leave` items never reach that table (see `payroll.ts`).
 */
export interface RealPayRunLineItemSnapshot {
  category: 'earnings' | 'benefit';
  amount_cents: number;
}

/** One GL line from `getPayRunJournalPreview`'s draft-only dry-run preview. */
export interface RealPayRunJournalPreviewLine {
  account_code: string;
  account_name: string;
  debit_cents: number;
  credit_cents: number;
}

/** `getPayRunJournalPreview`'s response, narrowed to what this adapter reads. Only ever resolved for `draft` runs (see module docblock). */
export interface RealPayRunJournalPreview {
  lines: RealPayRunJournalPreviewLine[];
  total_gross_cents: number;
  total_net_cents: number;
  total_employer_cost_cents: number;
  /**
   * WagePro worker types: set (> 0) when one or more of the draft's workers
   * has since been switched to 'contractor'. The API then returns an EMPTY
   * preview with zero totals rather than throwing (so every list/dashboard
   * keeps loading) — those zeros are placeholders, never real figures.
   * Absent on every unblocked preview.
   */
  blocked_contractor_count?: number;
}

/** The real backend pay-run statuses (`pay_runs.status` — `apps/api/src/db/schema/tenant/payroll.ts`). No `'in_review'`/`'filed'` equivalent exists; those two `PayRunStatus` members are mock-only and never emitted by this adapter. */
export type RealPayRunStatus = 'draft' | 'approved' | 'paid' | 'voided';

const VALID_REAL_PAY_RUN_STATUSES: readonly RealPayRunStatus[] = [
  'draft',
  'approved',
  'paid',
  'voided',
];

/**
 * `pay_runs.status` is a free-text `TEXT` column (`NOT NULL DEFAULT 'draft'`),
 * not a SQL CHECK-constrained enum — same untyped-wire situation as
 * `RealEmployeeRecord.employment_type` (see
 * `wagepro_store_employees.ts`'s `deriveEmploymentType`). This validates the
 * raw value against the closed set the UI's `PAY_RUN_STATUS_KEY`/
 * `PAY_RUN_STATUS_PILL` lookups (`lib/label_keys.ts`) require — an
 * unrecognised value there would render as `undefined`/a raw key or throw.
 * Defaults an unrecognised value to `'voided'`: an inert, terminal state
 * (no exceptions derived, no longer implicitly "editable"), the safest
 * assumption for data that should never occur rather than guessing `'draft'`
 * (which would imply it is still actionable).
 */
function deriveValidRealPayRunStatus(value: string): RealPayRunStatus {
  if (VALID_REAL_PAY_RUN_STATUSES.includes(value as RealPayRunStatus)) {
    return value as RealPayRunStatus;
  }
  // Should never happen (see doc comment above) — warn rather than silently
  // rendering corrupted data as an ordinary "Voided" pill with zero signal.
  console.warn(
    `[wagepro-web] pay run status "${value}" is not a recognised RealPayRunStatus — defaulting to 'voided'`,
  );
  return 'voided';
}

/**
 * A `pay_runs` row joined with its lines (`getPayRun`) and, for `draft` runs
 * only, its journal preview (`getPayRunJournalPreview`) — see the module
 * docblock for why this is a 3-call bundle rather than a single wire shape.
 * `data/use_pay_runs.ts` is the one place that assembles this per run.
 *
 * `status` is deliberately the raw wire string (not `RealPayRunStatus`) —
 * see {@link deriveValidRealPayRunStatus}; every `record.status === 'draft'`
 * comparison below is a safe raw-string check regardless (an unrecognised
 * value simply takes the "not draft" branch everywhere, which is the same
 * safe default `deriveValidRealPayRunStatus` itself resolves to).
 */
export interface RealPayRunRecord {
  id: string;
  period_start: string;
  period_end: string;
  status: string;
  total_gross_cents: number;
  created_at: string;
  /**
   * `pay_runs.approved_by` — the approving user's id, or `null` while the
   * run is a draft. A raw user id, never a display name: it is carried
   * through for completeness but never rendered (the approver's display
   * name comes from the pay-run audit feed instead — see
   * `approverDisplayNameFromRows` in `wagepro_store_pay_run_audit.ts`).
   */
  approved_by: string | null;
  /** `pay_runs.approved_at` — ISO timestamp `approvePayRun` stamped, or `null` while the run is a draft. Mapped onto `PayRun.approved_at`. */
  approved_at: string | null;
  lines: RealPayRunLine[];
  /** Non-null only when `status === 'draft'` — see `getPayRunJournalPreview`'s draft-only server contract. */
  journal_preview: RealPayRunJournalPreview | null;
}

/** The four SL statutory control-account codes the payroll GL posting always uses — see module docblock for why these are hardcoded rather than imported. */
const SL_EMPLOYEE_EPF_PAYABLE_CODE = '829';
const SL_ETF_PAYABLE_CODE = '831';
const SL_PAYE_PAYABLE_CODE = '832';
const SL_EMPLOYER_EPF_PAYABLE_CODE = '833';

/** Today's only supported jurisdiction's fixed statutory rates — matches the real SL engine's `SL_EMPLOYEE_EPF_BPS`/`SL_EMPLOYER_EPF_BPS`/`SL_EMPLOYER_ETF_BPS` exactly. See module docblock's "rate fields" gap note. */
const DEFAULT_STATUTORY_RATES = {
  epf_employee_rate: 800,
  epf_employer_rate: 1_200,
  etf_employer_rate: 300,
} as const;

/**
 * A validated `period_start`/`period_end`, or the date portion of
 * `created_at` when the input is absent/calendar-invalid — same "never
 * throw, always degrade to a parseable fallback" contract as
 * `wagepro_store_employees.ts`'s `deriveValidStartDate`.
 */
export function deriveValidPeriodDate(
  value: string,
  createdAt: string,
): string {
  return isParseableIsoDate(value) ? value : createdAt.slice(0, 10);
}

/**
 * Parses a validated `YYYY-MM-DD` string into a `PayPeriod`. Caller must have
 * already run the date through `deriveValidPeriodDate`, which guarantees
 * exactly 3 numeric `-`-separated parts.
 *
 * Exported for `wagepro_store_dashboard.ts` (C5, #21936), which needs the
 * same period-parsing for the activity feed's `pay_run_created`/
 * `pay_run_approved` events — single source of truth for "how does a pay
 * run's raw wire dates become a `PayPeriod`", never duplicated.
 */
export function derivePayPeriod(validIsoDate: string): PayPeriod {
  const [year, month] = validIsoDate.split('-').map(Number);
  return { year, month };
}

/**
 * How many of a DRAFT run's workers are now contractors (see
 * {@link RealPayRunJournalPreview.blocked_contractor_count}); `0` for an
 * unblocked draft, a draft with no resolved preview, and every approved+
 * run. When > 0, the run's preview totals are placeholder zeros: every
 * consumer that would otherwise print them as money (the statutory summary,
 * the wizard's totals card, the dashboard's amount-due) must show a blocked
 * state instead.
 *
 * Exported for `wagepro_store_dashboard.ts`, which must not price a statutory
 * deadline off a blocked draft's placeholder zeros.
 */
export function derivePayRunBlockedContractorCount(
  record: RealPayRunRecord,
): number {
  if (record.status !== 'draft') return 0;
  return record.journal_preview?.blocked_contractor_count ?? 0;
}

function sumJournalPreviewAccount(
  preview: RealPayRunJournalPreview,
  accountCode: string,
): MinorUnits {
  return preview.lines
    .filter((line) => line.account_code === accountCode)
    .reduce((sum, line) => sum + line.credit_cents, 0);
}

/**
 * Draft runs: recovered from the journal preview's GL lines (see module
 * docblock). Approved+ runs: summed straight from the lines' own persisted
 * statutory columns.
 *
 * Exported for `wagepro_store_dashboard.ts` (C5, #21936), which reuses this
 * EXACT computation (including the SL account-code matching caveats
 * documented above) to derive the dashboard's statutory-deadline card's
 * `amount_due` from the latest pay run — never a second, drifting
 * reimplementation of "how do we read a run's EPF/ETF/APIT totals off the
 * wire".
 */
export function deriveStatutorySummary(
  record: RealPayRunRecord,
): StatutorySummary {
  if (record.status === 'draft') {
    const preview = record.journal_preview;
    return {
      epf_employee: preview
        ? sumJournalPreviewAccount(preview, SL_EMPLOYEE_EPF_PAYABLE_CODE)
        : 0,
      epf_employer: preview
        ? sumJournalPreviewAccount(preview, SL_EMPLOYER_EPF_PAYABLE_CODE)
        : 0,
      etf_employer: preview
        ? sumJournalPreviewAccount(preview, SL_ETF_PAYABLE_CODE)
        : 0,
      apit_withheld: preview
        ? sumJournalPreviewAccount(preview, SL_PAYE_PAYABLE_CODE)
        : 0,
      ...DEFAULT_STATUTORY_RATES,
    };
  }

  const totals = record.lines.reduce(
    (acc, line) => ({
      epf_employee: acc.epf_employee + line.employee_epf_cents,
      epf_employer: acc.epf_employer + line.employer_epf_cents,
      etf_employer: acc.etf_employer + line.etf_cents,
      // `paye_cents` + the Table-2 lump-sum column, matching BOTH the
      // payslip's single "PAYE / APIT" row and `deriveLineStatutoryWithheld`
      // (which feeds the summary card's Statutory row). Summing `paye_cents`
      // alone made a lump-sum run show two disagreeing statutory figures in
      // the same wizard — the summary card's being the outlier (#24395).
      apit_withheld:
        acc.apit_withheld + line.paye_cents + (line.apit_lump_sum_cents ?? 0),
    }),
    { epf_employee: 0, epf_employer: 0, etf_employer: 0, apit_withheld: 0 },
  );
  return { ...totals, ...DEFAULT_STATUTORY_RATES };
}

/**
 * The allowance portion of one line's gross pay: its snapshotted `earnings`
 * pay items (#24395).
 *
 * `benefit` items are excluded deliberately — they are employer-borne cost
 * that never enters `gross_pay_cents` or net pay (see
 * `computeAssignedPayItems`' doc comment, `apps/api/src/trpc/routers/
 * payroll_pay_run_calculations.ts`). Including them would inflate the memo
 * beyond the gross it is meant to break down.
 *
 * Capped at the line's own `gross_pay_cents` for the same reason:
 * `createPayRun` folds earnings items into gross and THEN subtracts unpaid
 * leave with a floor at zero (`payroll.ts`), so an employee on full unpaid
 * leave keeps their allowance item rows against a gross of `0`. A memo can
 * never be larger than the figure it breaks down — reporting "gross 0, of
 * which allowances 50,000" would be nonsense on the card.
 */
export function deriveLineAllowances(line: RealPayRunLine): MinorUnits {
  const itemised = (line.items ?? []).reduce(
    (sum, item) =>
      item.category === 'earnings' ? sum + item.amount_cents : sum,
    0,
  );
  return Math.min(itemised, line.gross_pay_cents);
}

/** Run-level allowance memo: every line's `earnings` items (#24395). */
export function deriveTotalAllowances(record: RealPayRunRecord): MinorUnits {
  return record.lines.reduce(
    (sum, line) => sum + deriveLineAllowances(line),
    0,
  );
}

/**
 * One line's EMPLOYEE-side statutory withholding — byte-for-byte the same
 * five columns `generatePayslip` nets off before it prints a line's custom
 * deduction rows (`apps/api/src/trpc/routers/payroll.ts`, via
 * `computeCustomDeductionResidual`): SL `employee_epf + paye +
 * apit_lump_sum`, ID `pph21 + bpjs_employee`.
 *
 * Keeping this list identical to the payslip PDF's is the whole point
 * (#24395) — if the two ever diverge, the same pay run reports one split on
 * its summary card and a different one on the employee's payslip.
 * (`wagepro_store_my_pay.ts`'s in-app payslip view still omits
 * `apit_lump_sum`/`pph21`/`bpjs_employee` from its own residual, because it
 * reads the narrower `payslip_snapshots` table rather than `pay_run_lines`;
 * that is a separate surface, not something this module can source.)
 * Employer-side EPF/ETF is never included: it does not reduce net pay.
 *
 * The sum may legitimately be NEGATIVE for an Indonesian December/final-month
 * true-up, where `pph21_cents` is a refund that RAISES net pay — it is
 * deliberately not clamped, so the card keeps showing the real (negative)
 * withholding and `gross - statutory - other === net` still holds exactly.
 */
function deriveLineStatutoryWithheld(line: RealPayRunLine): MinorUnits {
  return (
    line.employee_epf_cents +
    line.paye_cents +
    (line.apit_lump_sum_cents ?? 0) +
    (line.pph21_cents ?? 0) +
    (line.bpjs_employee_cents ?? 0)
  );
}

/** A run's withheld total, split into the two rows the summary card shows. */
export interface PayRunDeductionSplit {
  /** Employee-side statutory withholding (see `deriveLineStatutoryWithheld`). */
  statutory: MinorUnits;
  /** Custom standing deductions — welfare fund, loan repayment, court order. */
  other: MinorUnits;
}

/**
 * Clamp a residual at 0 rather than reporting a deduction as a credit —
 * the same stance `computeCustomDeductionResidual` takes for the payslip.
 * A negative residual means the run's own columns contradict each other, so
 * it is warned about (ids only, never PII — docs/security-rules.md) rather
 * than swallowed. Clamping breaks the `gross - statutory - other === net`
 * identity, so `net_payable_reconciles` (`pay_run_calculations.ts`) goes
 * false and `PayRunSummaryCard` discloses the unreconciled breakdown in a
 * note — while still printing the authoritative `net_payable`. The clamp
 * must never cause the card to print a DERIVED net instead: that figure
 * contradicts what the employees are actually paid, and goes negative on a
 * draft line whose custom deductions exceed its gross.
 */
function clampResidual(
  rawCents: MinorUnits,
  payRunId: string,
  label: string,
): MinorUnits {
  if (rawCents < 0) {
    console.warn(
      `[wagepro-web] negative ${label} residual ${String(rawCents)} clamped to 0 (pay_run_id=${payRunId})`,
    );
    return 0;
  }
  return rawCents;
}

/**
 * Split a run's total withholding (`grossWages - netPayable`) into statutory
 * and custom (#24395). Exactly one side of the split is read authoritatively
 * and the other is the remainder, so the two always add back up to the
 * withheld total and the card reconciles by construction:
 *
 * - **draft**: the statutory columns are genuinely all zero until
 *   `approvePayRun` computes them, but `pay_run_lines.deductions_cents` at
 *   this point holds ONLY the custom deductions applied at pay-run creation
 *   time (`statutory_computation_service.ts` adds statutory ON TOP of it
 *   later, never replacing it). So custom is authoritative and statutory is
 *   the remainder of the journal preview's post-statutory totals — which
 *   works for every jurisdiction, including Indonesia, whose PPh 21/BPJS
 *   liabilities post to per-entity-resolved accounts that
 *   `deriveStatutorySummary`'s SL account-code matching cannot see.
 * - **approved / paid / voided**: `deductions_cents` has been rewritten to
 *   the combined total, so the per-line statutory columns are authoritative
 *   (mirroring the payslip) and custom is the remainder.
 */
export function deriveDeductionSplit(
  record: RealPayRunRecord,
  grossWages: MinorUnits,
  netPayable: MinorUnits,
): PayRunDeductionSplit {
  const withheld = grossWages - netPayable;

  if (record.status === 'draft') {
    const other = record.lines.reduce(
      (sum, line) => sum + line.deductions_cents,
      0,
    );
    return {
      statutory: clampResidual(withheld - other, record.id, 'statutory'),
      other,
    };
  }

  const statutory = record.lines.reduce(
    (sum, line) => sum + deriveLineStatutoryWithheld(line),
    0,
  );
  return {
    statutory,
    other: clampResidual(withheld - statutory, record.id, 'custom-deduction'),
  };
}

function toLineItem(
  line: RealPayRunLine,
  employeesById: ReadonlyMap<string, RealEmployeeRecord>,
): PayRunLineItem {
  return {
    employee_id: line.employee_id,
    employee_name: employeesById.get(line.employee_id)?.full_name ?? '—',
    gross: line.gross_pay_cents,
    // A memo breakdown of `gross` (the engine folded these earnings items
    // into `gross_pay_cents` itself), never an amount on top — see
    // `deriveLineAllowances` and `PayRunLineItem.allowances`.
    allowances: deriveLineAllowances(line),
    // Total of every deduction (custom + statutory combined) so that
    // gross + allowances - deductions === net_pay_cents exactly — see
    // module docblock.
    deductions: line.gross_pay_cents - line.net_pay_cents,
    // #23309 — surface the two per-line, decide-after-the-fact deltas so
    // the wizard's Review Pay edit affordance can pre-populate the current
    // value in-place rather than start from a blank slate.
    adjustment_cents: line.adjustment_cents ?? null,
    adjustment_reason: line.adjustment_reason ?? null,
    lump_sum_cents: line.lump_sum_cents,
    // V387 -- surfaced as row hints on the review table. Back pay is INSIDE
    // `gross`; these never add to it.
    back_pay_cents: line.back_pay_cents ?? 0,
    salary_changed_in_period: hasSalaryChangedInPeriod(
      line.compensation_segments,
    ),
  };
}

function isBankDetailsIncomplete(employee: RealEmployeeRecord): boolean {
  return (
    employee.bank_name === null ||
    employee.bank_name === '' ||
    employee.bank_account_number === null ||
    employee.bank_account_number === ''
  );
}

/**
 * An active employee who joined within this run's own pay period AND has no
 * pay rate configured yet — a salaried joiner with no monthly salary set, or
 * an hourly joiner with a zero hourly rate. Purely derived from the
 * already-resolved employee records; no new backend call.
 */
function isNewJoinerUnconfigured(
  employee: RealEmployeeRecord,
  periodStart: string,
  periodEnd: string,
): boolean {
  const startDate = employee.employment_start_date;
  if (startDate === null || !isParseableIsoDate(startDate)) return false;
  // YYYY-MM-DD strings compare lexicographically exactly like calendar dates.
  const joinedWithinPeriod = startDate >= periodStart && startDate <= periodEnd;
  if (!joinedWithinPeriod) return false;

  return employee.pay_basis === 'salaried'
    ? employee.monthly_salary_cents === null
    : employee.hourly_rate_cents === 0;
}

/**
 * `periodStart`/`periodEnd` MUST be the already-validated dates
 * (`deriveValidPeriodDate`'s output), never `record.period_start`/
 * `record.period_end` directly — a calendar-invalid raw period bound would
 * otherwise silently corrupt the new-joiner comparison below (and diverge
 * from the period this same run displays everywhere else).
 */
function deriveExceptions(
  record: RealPayRunRecord,
  employeeRecords: readonly RealEmployeeRecord[] | undefined,
  periodStart: string,
  periodEnd: string,
): PayRunException[] {
  const exceptions: PayRunException[] = [];

  // #24397: derived from the LINE, not the employee — the contradiction is a
  // property of what this particular run computed (the employee's leave
  // register and timesheet disagreed inside THIS period), and the backend has
  // already resolved it into a number. An employee with a clean timesheet next
  // period raises nothing.
  //
  // Deliberately ABOVE the `employeeRecords` guard below: this exception gates
  // the wizard on an unacknowledged pay conflict, so an unresolved employee
  // list must degrade it to "no NAME", never to "no exception" — otherwise a
  // slow/failed employee fetch silently lets an admin advance past it. The
  // employee-derived exceptions after the guard genuinely cannot be computed
  // without that list and still degrade to `[]`.
  const employeeNameById = new Map(
    (employeeRecords ?? []).map((employee) => [
      employee.id,
      employee.full_name,
    ]),
  );
  for (const line of record.lines) {
    if ((line.unpaid_leave_hours_excluded ?? 0) <= 0) continue;
    exceptions.push({
      id: `${record.id}-unpaidhours-${line.employee_id}`,
      kind: 'time_logged_on_unpaid_leave',
      employee_name: employeeNameById.get(line.employee_id) ?? null,
      acknowledged: false,
    });
  }

  if (!employeeRecords) return exceptions;

  for (const employee of employeeRecords) {
    // A terminated/deactivated employee is not part of an upcoming run.
    if (employee.termination_date !== null || !employee.is_active) continue;

    if (isBankDetailsIncomplete(employee)) {
      exceptions.push({
        id: `${record.id}-bank-${employee.id}`,
        kind: 'bank_details_incomplete',
        employee_name: employee.full_name,
        acknowledged: false,
      });
    }
    if (isNewJoinerUnconfigured(employee, periodStart, periodEnd)) {
      exceptions.push({
        id: `${record.id}-joiner-${employee.id}`,
        kind: 'new_joiner_unconfigured',
        employee_name: employee.full_name,
        acknowledged: false,
      });
    }
  }
  return exceptions;
}

function deriveApproval(record: RealPayRunRecord): PayRunApproval {
  const hasBeenApproved = record.status !== 'draft';
  return {
    preparer_name: '',
    preparer_state: 'submitted',
    approver_name: '',
    approver_title: '',
    approver_state: hasBeenApproved ? 'approved' : 'not_submitted',
  };
}

/**
 * Run-level gross/net. For an `approved`+ run, `lines[].net_pay_cents` is
 * already the real, persisted, post-statutory figure — sum it directly.
 *
 * For a **draft** run, `lines[].net_pay_cents` is `pay_run_lines`' value as
 * of pay-run CREATION time — genuinely pre-statutory; EPF/PAYE is only
 * computed and persisted by `approvePayRun` (see
 * `statutory_computation_service.ts`'s own doc comment: "Net pay as of
 * pay-run creation time (pre-statutory)"). Summing those lines for a draft
 * run's `net_payable` would silently show a bigger, wrong number that
 * contradicts the SAME run's `statutory` panel (which IS post-statutory,
 * sourced from this identical `journal_preview`) — a draft run with real
 * EPF+PAYE withholding would show "statutory deductions: 0.00" next to a
 * "pending gross" net payable, while the Statutory step two clicks away
 * shows the true non-zero EPF/PAYE breakdown. Using the journal preview's
 * own `total_gross_cents`/`total_net_cents` ties both figures to the exact
 * same computation, so they can never disagree.
 *
 * When no `journal_preview` was resolved (e.g. statutory accounts not yet
 * seeded for this entity — see `getPayRunJournalPreview`'s own fallback),
 * this falls back to the pre-statutory line sum, which is honestly
 * consistent with `deriveStatutorySummary` ALSO returning all-zero
 * statutory figures in that same case — both sides of the reconciliation
 * degrade together, never independently.
 *
 * ── Indonesian THR (#24395) ───────────────────────────────────────────────
 *
 * `pay_runs.total_gross_cents` NEVER includes THR: `postTHR` writes only
 * `pay_run_lines.thr_cents` (plus the run's `thr_holiday_date`) and leaves
 * the run-level gross alone, while `approvePayRun` persists
 * `net_pay_cents = max(0, gross_pay_cents + thr_cents - deductions)`
 * (`apps/api/src/trpc/routers/payroll.ts`). So for an approved+ run the THR
 * must be added back into gross here — otherwise `gross - net` understates
 * the withheld total by the THR, which both under-reports Gross wages and
 * (because the statutory side is read authoritatively from the line
 * columns) drives the custom-deduction remainder negative on any ordinary
 * ID run where THR exceeds the employee's standing deductions.
 *
 * A DRAFT run's `net_pay_cents` predates THR entirely (it is the
 * creation-time figure; `postTHR` never recomputes it) and the journal
 * preview's own gross/net totals exclude THR on both sides, so THR is
 * deliberately NOT added on the draft paths — adding it there would invent
 * withholding that the draft's own net does not reflect.
 *
 * WHY that leaves a known draft-ID inversion, and why the fix does NOT
 * belong here: the journal preview's PPh 21 is computed on a `gross + thr`
 * TER base (`apps/api/src/trpc/routers/payroll.ts`) even though the same
 * preview's `total_gross_cents`/`total_net_cents` exclude the THR. So a
 * draft Indonesian run with a posted THR overstates statutory withholding
 * and understates gross and net by that THR. That inconsistency is the
 * API's — the preview's tax base and its own totals disagree — and the only
 * correct fix is to make the preview include THR in its gross/net (or
 * exclude it from the TER base) on the API side. Adding THR to gross HERE
 * would not fix it: the preview's net excludes THR, so the withheld total
 * (`gross - net`) would grow by the whole THR and land in the custom
 * "Other deductions" remainder, replacing an understated card with a
 * fabricated deduction. Leave this alone until the API is corrected.
 */
function deriveGrossAndNet(record: RealPayRunRecord): {
  grossWages: MinorUnits;
  netPayable: MinorUnits;
} {
  if (record.status === 'draft' && record.journal_preview) {
    return {
      grossWages: record.journal_preview.total_gross_cents,
      netPayable: record.journal_preview.total_net_cents,
    };
  }
  // Draft (no preview): `net_pay_cents` is the pre-statutory, pre-THR
  // creation-time figure, so gross stays exactly as persisted.
  const thrTotal =
    record.status === 'draft'
      ? 0
      : record.lines.reduce((sum, line) => sum + (line.thr_cents ?? 0), 0);
  return {
    grossWages: record.total_gross_cents + thrTotal,
    netPayable: record.lines.reduce((sum, line) => sum + line.net_pay_cents, 0),
  };
}

function toUiPayRun(
  record: RealPayRunRecord,
  employeeRecords: readonly RealEmployeeRecord[] | undefined,
): PayRun {
  const employeesById = new Map(
    (employeeRecords ?? []).map((employee) => [employee.id, employee]),
  );

  const periodStart = deriveValidPeriodDate(
    record.period_start,
    record.created_at,
  );
  const periodEnd = deriveValidPeriodDate(record.period_end, record.created_at);

  const { grossWages, netPayable } = deriveGrossAndNet(record);
  // A memo breakdown of `grossWages`, never added on top of it (#24395).
  const totalAllowances = deriveTotalAllowances(record);
  // Reconciles by construction: derived_net_payable(run) === net_payable,
  // i.e. gross - statutory - other === net.
  const deductionSplit = deriveDeductionSplit(record, grossWages, netPayable);

  const status: PayRunStatus = deriveValidRealPayRunStatus(record.status);

  return {
    id: record.id,
    created_at: record.created_at,
    period: derivePayPeriod(periodStart),
    currency: 'LKR',
    gross_wages: grossWages,
    total_allowances: totalAllowances,
    statutory_deductions: deductionSplit.statutory,
    other_deductions: deductionSplit.other,
    net_payable: netPayable,
    employee_count: record.lines.length,
    status,
    // A draft has never been approved, so any stray value is ignored.
    approved_at: record.status === 'draft' ? null : record.approved_at,
    // Documented gap: no distinct pay_date/filing-calendar concept yet.
    pay_date: periodEnd,
    period_start: periodStart,
    period_end: periodEnd,
    filing_due_on: periodEnd,
    // NOTE: per-line `deductions`/net (see `toLineItem`) stay pre-statutory
    // for a draft run — the journal preview only returns AGGREGATE GL
    // totals, never a per-employee breakdown, so the per-row figures in the
    // Review Pay step legitimately differ from the run-level totals above
    // until the run is approved. This is a real, unavoidable gap (there is
    // no per-line statutory data to show pre-approval), not a
    // reconciliation bug — see the module docblock's "Documented gaps".
    line_items: record.lines.map((line) => toLineItem(line, employeesById)),
    statutory: deriveStatutorySummary(record),
    exceptions:
      record.status === 'draft'
        ? deriveExceptions(record, employeeRecords, periodStart, periodEnd)
        : [],
    approval: deriveApproval(record),
    blocked_contractor_count: derivePayRunBlockedContractorCount(record),
  };
}

/**
 * Derives the UI's pay-run list from real backend records, or passes the
 * mock list through UNCHANGED when `records` is `undefined` — mirrors
 * `deriveEmployeesFromRecords`'s "absent input -> no-op" contract exactly,
 * so an un-migrated call site (or a dev boot where the pay-runs resolver
 * failed) keeps today's fully-mocked pay-run history instead of silently
 * going empty.
 *
 * An explicitly EMPTY array is a real, distinct state from "no data
 * provided" — an entity with no pay runs yet renders the existing empty
 * states (`pay_run.none_open_*` in `run_payroll_screen.tsx`), it does NOT
 * fall back to the mock history.
 *
 * `employeeRecords` is the already-resolved `RealEmployeeRecord[]` from C3
 * (#21934) — passed through untouched for exception derivation (see module
 * docblock). Its absence does not affect the "mock unchanged" contract
 * above, which is governed by `records` alone; when `records` is real but
 * `employeeRecords` failed to resolve, every run's EMPLOYEE-derived exception
 * (incomplete bank details, unconfigured new joiner) degrades to nothing,
 * while the LINE-derived `time_logged_on_unpaid_leave` exception (#24397)
 * still surfaces with a null name — it gates the wizard on a pay conflict and
 * must never disappear just because a name lookup failed (see
 * `deriveExceptions`).
 *
 * There is no audit-rows parameter here — C10 (#22332) moved audit
 * resolution to a real, on-demand hook (`use_pay_run_audit.ts`) the drawer
 * calls directly, keyed on `PayRun.created_at`; a boot-time, per-run audit
 * map has no remaining caller (see module docblock's "Documented gaps").
 */
export function derivePayRunsFromRecords(
  mock: PayRun[],
  records: RealPayRunRecord[] | undefined,
  employeeRecords: readonly RealEmployeeRecord[] | undefined,
): PayRun[] {
  if (records === undefined) return mock;
  return records.map((record) => toUiPayRun(record, employeeRecords));
}
